/* ---- <shop-the-look> ---- */
(() => {
  'use strict';
  if (customElements.get('shop-the-look')) return;

  const AUTO = [[28, 20], [76, 38], [22, 78], [64, 70], [46, 36], [84, 82], [36, 54], [70, 14]];

  const PHONE = window.matchMedia('(max-width: 749px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class ShopTheLook extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      // Set-up (measuring, observers, carousels) waits until the section nears the viewport (theme.js), so it
      // stays off the main thread while the first screen loads.
      const near = window.theme?.whenNear;
      if (near) near(this, () => { if (this.isConnected && !this.started) this.setup(); });
      else this.setup();
    }

    setup() {
      this.started = true;
      this.panel = this.querySelector('[data-stl-panel]');
      this.carousel = this.querySelector('ft-carousel');
      this.counter = this.querySelector('[data-stl-current]');
      this.openIndex = (parseInt(this.dataset.open, 10) || 0) - 1;
      this.active = -1;

      this.placeHotspots();
      this.addEventListener('click', (e) => this.onClick(e));
      this.carousel?.addEventListener('ftc:change', (e) => this.onChange(e.detail.index));
      this.onKey = (e) => { if (e.key === 'Escape' && this.sheetShown) this.hideSheet(true); };
      this.onOutside = (e) => {
        if (!this.sheetShown || this.panel.contains(e.target) || e.target.closest('[data-stl-hotspot]')) return;
        this.hideSheet(false);
      };
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('click', this.onOutside);
      this.onMedia = () => { if (!PHONE.matches) this.hideSheet(false, true); this.render(); };
      PHONE.addEventListener('change', this.onMedia);

      if (window.Shopify?.designMode) {
        this.closest('.shopify-section')?.addEventListener('shopify:block:select', (e) => {
          const i = this.cards.findIndex((c) => c === e.target || c.contains(e.target) || e.target.contains(c));
          if (i < 0) return;
          if (PHONE.matches) this.showSheet(i); else this.setOpen(i, true);
        });
      }

      this.render();
    }

    disconnectedCallback() {
      this.started = false;
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('click', this.onOutside);
      PHONE.removeEventListener('change', this.onMedia);
    }

    placeHotspots() {
      const media = this.querySelector('.stl__media');
      if (!media) return;
      this.cards.forEach((card, i) => {
        const spot = card.querySelector(':scope > [data-stl-hotspot]');
        if (!spot) return;
        let x = parseFloat(spot.dataset.x), y = parseFloat(spot.dataset.y);
        if (spot.hasAttribute('data-auto') || Number.isNaN(x) || Number.isNaN(y)) {
          const [ax, ay] = AUTO[i % AUTO.length];
          const lap = Math.floor(i / AUTO.length) * 6;
          x = Math.min(92, ax + lap); y = Math.min(92, ay + lap);
        }
        spot.dataset.stlHotspot = String(i);
        spot.style.left = x + '%';
        spot.style.top = y + '%';
        media.appendChild(spot);
      });
    }

    get cards() { return Array.from(this.querySelectorAll('[data-stl-card]')); }
    get hotspots() { return Array.from(this.querySelectorAll('.stl__media > [data-stl-hotspot]')); }

    onClick(e) {
      const step = e.target.closest('[data-stl-qty]');
      if (step) {
        const input = step.closest('.stl-qty')?.querySelector('input');
        if (input && !input.disabled) input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(step.dataset.stlQty));
        return;
      }
      if (e.target.closest('[data-stl-close]')) { this.hideSheet(true); return; }

      const hotspot = e.target.closest('[data-stl-hotspot]');
      if (hotspot) {
        const i = Number(hotspot.dataset.stlHotspot);
        this.opener = hotspot;
        if (PHONE.matches) this.showSheet(i);
        else this.setOpen(i === this.openIndex ? -1 : i, true);
        return;
      }

      const toggle = e.target.closest('[data-stl-toggle]');
      if (toggle) this.setOpen(this.cards.indexOf(toggle.closest('[data-stl-card]')));
    }

    /* ---- Desktop / tablet ---- */
    setOpen(i, fromHotspot = false) {
      this.openIndex = i;
      this.render();
      const card = this.cards[i];
      if (fromHotspot && card) {
        const r = card.getBoundingClientRect();
        if (r.top < 0 || r.bottom > window.innerHeight) card.scrollIntoView({ block: 'nearest', behavior: REDUCED ? 'auto' : 'smooth' });
      }
    }

    render() {
      const phone = PHONE.matches;
      const shown = phone ? (this.sheetShown ? this.active : -1) : this.openIndex;
      this.cards.forEach((card, i) => {
        const open = !phone && i === this.openIndex;
        card.classList.toggle('is-open', open);
        card.querySelector('[data-stl-toggle]')?.setAttribute('aria-expanded', String(open));
        const more = card.querySelector('.stl-card__more');
        if (more) more.inert = !phone && !open;
      });
      this.hotspots.forEach((h, i) => h.setAttribute('aria-expanded', String(i === shown)));
    }

    /* ---- Phones ---- */
    get sheetShown() { return this.panel?.classList.contains('is-shown'); }

    showSheet(i) {
      if (!this.panel) return;
      this.active = i;
      if (this.sheetShown) {
        this.carousel?.go(i);
      } else {
        clearTimeout(this.hideTimer);
        this.panel.classList.add('is-shown');
        this.carousel?.go(i, { instant: true });
        void this.panel.offsetHeight;
        requestAnimationFrame(() => this.panel.classList.add('is-open'));
        this.panel.querySelector('[data-stl-close]')?.focus({ preventScroll: true });
      }
      this.updateCount();
      this.render();
    }

    hideSheet(returnFocus, now = false) {
      if (!this.panel || !this.sheetShown) return;
      this.panel.classList.remove('is-open');
      const done = () => { this.panel.classList.remove('is-shown'); };
      if (now || REDUCED) done(); else this.hideTimer = setTimeout(done, 450);
      this.active = -1;
      this.render();
      if (returnFocus) this.opener?.focus({ preventScroll: true });
    }

    onChange(i) {
      if (!PHONE.matches || !this.sheetShown || i === this.active) return;
      this.active = i; this.updateCount(); this.render();
    }

    updateCount() { if (this.counter) this.counter.textContent = String(this.active + 1); }
  }

  customElements.define('shop-the-look', ShopTheLook);
})();
